import type { GleanInstallerConfig } from "../api/gleanHandoff";
import { detectGleanPlatform, gleanInstallerUrl } from "./gleanPlatform";

const desktopLabels = { windows: "Windows", macos: "macOS", linux: "Linux" } as const;
const requirements = {
  windows: "Requires Windows 10/11, Microsoft Edge and Node.js 24.",
  macos: "Use the approved macOS installer and follow its requirements before returning to Velocity.",
  linux: "Use the approved Linux installer and follow its requirements before returning to Velocity.",
} as const;

/** The modal and Settings share the same device-specific, server-authorized download. */
export function GleanInstallerDownload({ config, loaded, error = false, onRetry }: {
  config: GleanInstallerConfig | null;
  loaded: boolean;
  error?: boolean;
  onRetry?: () => void;
}) {
  const platform = detectGleanPlatform();
  const desktop = platform === "windows" || platform === "macos" || platform === "linux" ? platform : null;
  const label = desktop ? `Download ${desktopLabels[desktop]} installer` : "Download installer";
  const url = gleanInstallerUrl(platform, config);
  const unavailable = platform === "unknown"
    ? "We couldn't identify this device's operating system. Contact your administrator for a compatible Glean automation installer."
    : !desktop
      ? "Glean desktop automation is not available on this mobile device. Use a supported desktop or laptop."
      : `The ${desktopLabels[desktop]} installer is not available yet. Contact your administrator for an approved installer for this operating system.`;
  return (
    <div className="space-y-2 text-sm text-[var(--color-text-secondary)]">
      {url && desktop ? <>
        <p>First time on this laptop? Install the Glean Helper once, then return here to continue.</p>
        <p>{requirements[desktop]}</p>
        <a href={url} target="_blank" rel="noopener noreferrer" download className="inline-block rounded bg-[var(--color-accent)] px-3 py-2 font-medium text-black">{label}</a>
        <p>Run the downloaded installer once, then return to Velocity. Downloading alone does not install or start automation.</p>
      </> : <>
        <button type="button" disabled className="rounded bg-[var(--color-accent)] px-3 py-2 font-medium text-black opacity-50">{label}</button>
        <p role="status">{error && desktop ? "We couldn't check installer availability. Try again, or contact your administrator." : loaded ? unavailable : "Checking installer availability…"}</p>
        {error && desktop && onRetry && <button type="button" onClick={onRetry} className="rounded border px-3 py-2">Retry download check</button>}
      </>}
      <details>
        <summary>Download blocked by your organization?</summary>
        <p>Contact your IT team to review and approve the Glean Helper download. Share the download address and the block message with them.</p>
      </details>
    </div>
  );
}
